import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd'
import { adminApi, adminLongRunningApi } from '../../api'
import { KNOWLEDGE_SCOPE_OPTIONS_SHORT } from '../../constants/knowledgeScope'
import { PageHeader } from '../../components/ui'
import { AgentPromptLink } from './AgentPromptLink'
import { LinkRulesManage } from './LinkRulesManage'

type KeyValueExtractCfg = {
  enabled: boolean
  agent_code: string
  max_value_chars: number
  min_value_chars: number
}

type AgentOption = {
  agent_code: string
  agent_name: string
}

type KeyValueRow = {
  id: number
  key: string
  value: string
  source: string
  for_suggest: boolean
  for_relation: boolean
}

const DEFAULT_AGENT_CODE = 'key_value_extract'

export default function AttributeExtract() {
  const [kvForm] = Form.useForm<KeyValueExtractCfg>()
  const [kvLoading, setKvLoading] = useState(false)
  const [kvSaving, setKvSaving] = useState(false)
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([])
  const [testItemId, setTestItemId] = useState('')
  const [testScope, setTestScope] = useState('enterprise')
  const [testExtracting, setTestExtracting] = useState(false)
  const [kvRebuilding, setKvRebuilding] = useState(false)
  const [testRows, setTestRows] = useState<KeyValueRow[]>([])
  const [testTotal, setTestTotal] = useState(0)

  const kvEnabled = Form.useWatch('enabled', kvForm) ?? true
  const boundAgentCode = (Form.useWatch('agent_code', kvForm) as string | undefined) || DEFAULT_AGENT_CODE

  const agentSelectOptions = useMemo(
    () =>
      agentOptions.map((a) => ({
        value: a.agent_code,
        label: `${a.agent_name}（${a.agent_code}）`,
      })),
    [agentOptions],
  )

  const boundAgentName =
    agentOptions.find((a) => a.agent_code === boundAgentCode)?.agent_name || boundAgentCode

  useEffect(() => {
    void adminApi
      .get<{ items: AgentOption[] }>('/admin/agents/options', {
        params: { active_only: true, page_size: 200 },
      })
      .then((r) => setAgentOptions(r.data.items ?? []))
      .catch(() => setAgentOptions([]))
  }, [])

  useEffect(() => {
    setKvLoading(true)
    adminApi
      .get<KeyValueExtractCfg & { system_prompt?: string }>('/admin/settings/key-value-extract')
      .then((r) =>
        kvForm.setFieldsValue({
          enabled: r.data.enabled,
          agent_code: r.data.agent_code || DEFAULT_AGENT_CODE,
          max_value_chars: r.data.max_value_chars,
          min_value_chars: r.data.min_value_chars,
        }),
      )
      .catch(() => message.error('加载属性抽取配置失败'))
      .finally(() => setKvLoading(false))
  }, [kvForm])

  const saveKvCfg = () => {
    void kvForm.validateFields().then((v) => {
      setKvSaving(true)
      adminApi
        .put<KeyValueExtractCfg>('/admin/settings/key-value-extract', {
          enabled: v.enabled,
          agent_code: v.agent_code,
          max_value_chars: v.max_value_chars,
          min_value_chars: v.min_value_chars,
        })
        .then((r) => {
          message.success('属性抽取配置已保存')
          kvForm.setFieldsValue({
            enabled: r.data.enabled,
            agent_code: r.data.agent_code || DEFAULT_AGENT_CODE,
            max_value_chars: r.data.max_value_chars,
            min_value_chars: r.data.min_value_chars,
          })
        })
        .catch((e) => message.error(e instanceof Error ? e.message : '保存失败'))
        .finally(() => setKvSaving(false))
    })
  }

  const loadTestKeyValues = (itemId: number, scope = testScope) => {
    return adminApi
      .get<{ items: KeyValueRow[]; total: number }>(
        `/admin/knowledge/items/${itemId}/key-values`,
        { params: { scope, page: 1, page_size: 50 } },
      )
      .then((r) => {
        setTestRows(r.data.items || [])
        setTestTotal(r.data.total || 0)
      })
  }

  const runTestExtract = () => {
    const id = Number.parseInt(testItemId.trim(), 10)
    if (!Number.isFinite(id) || id <= 0) {
      message.warning('请输入有效的知识条目 ID')
      return
    }
    setTestExtracting(true)
    message.info('正在抽取属性，大文档可能需要数分钟，请稍候…', 4)
    adminLongRunningApi
      .post<{
        item_id: number
        count: number
        suggest_count?: number
        relation_count?: number
        llm?: number
        llm_error?: string
        skipped?: boolean
      }>(
        `/admin/knowledge/items/${id}/key-values/extract`,
        {},
        { params: { scope: testScope } },
      )
      .then(async (r) => {
        const d = r.data
        if (d.skipped && d.llm_error) {
          message.error(`抽取失败：${d.llm_error}（已保留原有属性）`)
        } else if (d.llm_error) {
          message.warning(
            `抽取完成（LLM 部分失败：${d.llm_error}），共 ${d.count} 条，联想 ${d.suggest_count ?? 0} 条，关联 ${d.relation_count ?? 0} 条`,
          )
        } else {
          message.success(
            `抽取完成，共 ${d.count} 条，联想 ${d.suggest_count ?? 0} 条，关联 ${d.relation_count ?? 0} 条（已写 MySQL 与 ES）`,
          )
        }
        await loadTestKeyValues(id, testScope)
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '抽取失败'))
      .finally(() => setTestExtracting(false))
  }

  const rebuildSuggestIndex = () => {
    setKvRebuilding(true)
    adminApi
      .post<{ scopes: Record<string, { ok: boolean; synced: number; items: number }> }>(
        '/admin/knowledge/key-values/rebuild-suggest-index',
        {},
        { params: { scope: testScope } },
      )
      .then((r) => {
        const row = r.data.scopes?.[testScope]
        if (row?.ok) {
          message.success(`联想索引已重建（${testScope}），回灌 ${row.synced} 条 / ${row.items} 个条目`)
        } else {
          message.error('联想索引重建失败，请确认 ES 与 IK 分词插件')
        }
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '重建失败'))
      .finally(() => setKvRebuilding(false))
  }

  return (
    <div>
      <PageHeader
        title="属性抽取"
        description="从文档抽取 key/value 属性，并维护跨属性关联规则（用于相关文档推荐）；抽取提示词在绑定智能体中配置。"
      />
      <Tabs
        items={[
          {
            key: 'extract',
            label: '抽取配置',
            children: (
              <Card loading={kvLoading}>
        <Form form={kvForm} layout="vertical" initialValues={{ agent_code: DEFAULT_AGENT_CODE }}>
          <Space size="large" wrap>
            <Form.Item name="enabled" label="启用属性抽取" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item
            name="agent_code"
            label="绑定智能体"
            rules={[{ required: true, message: '请选择智能体' }]}
            extra="抽取使用的提示词与模型取自该智能体；默认可选内置「属性抽取」。"
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={agentSelectOptions}
              placeholder="选择智能体"
              disabled={!kvEnabled}
            />
          </Form.Item>
          {boundAgentCode ? (
            <AgentPromptLink
              key={boundAgentCode}
              agentCode={boundAgentCode}
              agentName={boundAgentName}
            />
          ) : null}
          <Space size="large" wrap>
            <Form.Item name="max_value_chars" label="属性值最大字数">
              <InputNumber min={32} max={1024} disabled={!kvEnabled} />
            </Form.Item>
            <Form.Item name="min_value_chars" label="属性值最小字数">
              <InputNumber min={1} max={32} disabled={!kvEnabled} />
            </Form.Item>
          </Space>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            整篇解析正文一次性送入对话模型，不再分段或截断；超长文档依赖模型上下文上限。
            {boundAgentCode ? (
              <>
                {' '}
                编辑绑定智能体：<Link to="/manage/agents">智能体列表</Link>
              </>
            ) : null}
          </Typography.Paragraph>
          <Space wrap>
            <Button type="primary" loading={kvSaving} onClick={saveKvCfg}>
              保存配置
            </Button>
            <Button loading={kvRebuilding} onClick={rebuildSuggestIndex}>
              重建联想索引
            </Button>
          </Space>
        </Form>
        <Typography.Title level={5} style={{ marginTop: 24 }}>
          验证抽取（已索引条目）
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          手动触发抽取：先删该条目旧数据，再写 MySQL 与 ES（含 for_suggest / for_relation）。大文档可能需数分钟。
        </Typography.Paragraph>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            value={testScope}
            style={{ width: 140 }}
            options={KNOWLEDGE_SCOPE_OPTIONS_SHORT}
            onChange={setTestScope}
          />
          <Input
            style={{ width: 160 }}
            placeholder="条目 ID"
            value={testItemId}
            onChange={(e) => setTestItemId(e.target.value)}
            onPressEnter={runTestExtract}
          />
          <Button type="primary" loading={testExtracting} onClick={runTestExtract}>
            立即抽取
          </Button>
          <Button
            onClick={() => {
              const id = Number.parseInt(testItemId.trim(), 10)
              if (!Number.isFinite(id) || id <= 0) {
                message.warning('请输入有效的知识条目 ID')
                return
              }
              void loadTestKeyValues(id, testScope).catch((e) =>
                message.error(e instanceof Error ? e.message : '加载失败'),
              )
            }}
          >
            刷新列表
          </Button>
        </Space>
        <Table<KeyValueRow>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={testRows}
          columns={[
            { title: 'key', dataIndex: 'key', width: 140, ellipsis: true },
            { title: 'value', dataIndex: 'value', ellipsis: true },
            {
              title: '联想',
              dataIndex: 'for_suggest',
              width: 72,
              render: (v: boolean) => (v ? '是' : '否'),
            },
            {
              title: '可关联',
              dataIndex: 'for_relation',
              width: 72,
              render: (v: boolean) => (v ? '是' : '否'),
            },
            { title: '来源', dataIndex: 'source', width: 100 },
          ]}
          locale={{ emptyText: testItemId ? '暂无数据，请先点「立即抽取」' : '输入 ID 后抽取' }}
        />
        {testTotal > testRows.length ? (
          <Typography.Text type="secondary">共 {testTotal} 条，仅显示前 {testRows.length} 条</Typography.Text>
        ) : null}
              </Card>
            ),
          },
          {
            key: 'link-rules',
            label: '关联规则',
            children: <LinkRulesManage />,
          },
        ]}
      />
    </div>
  )
}
